import { GetQueryVariables } from "api/apiFunctions";
import { deleteRequest } from "api/commonActions/delete";
import { getRequest } from "api/commonActions/get";
import { postRequest } from "api/commonActions/post";
import { patchRequest } from "api/commonActions/update";
import { Requests } from "config/api";
import { GetProps } from "declarations/commonServices";
import { VideoPostStatusType, VideoPostType } from "types/loadedData";

type VideoPayload = {
  title?: string;
  description?: string;
  status?: VideoPostStatusType;
  car?: string;
  thumbnailUrl?: string;
  video?: File | null;
};

type VideoListReturn = {
  status?: number | string;
  message?: string;
  videos: VideoPostType[];
  count: number;
};

type VideoReturn = {
  status?: number | string;
  message?: string;
  video?: VideoPostType;
};

const buildVideoForm = (payload: VideoPayload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key === "video" && value instanceof File) {
      formData.append(key, value);
      return;
    }
    formData.append(key, String(value));
  });

  return formData;
};

export const GetVideos = async ({
  limit,
  offset,
  search,
  setLoading,
}: GetProps): Promise<VideoListReturn> => {
  const queryParams = GetQueryVariables({ limit, offset, search });

  try {
    setLoading && setLoading(true);
    const response = await getRequest({
      route: Requests.get.getVideos,
      queryParams,
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      videos: response?.data?.videos || [],
      count: response?.data?.count || 0,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const CreateVideo = async (
  payload: VideoPayload
): Promise<VideoReturn> => {
  const response = await postRequest({
    route: Requests.post.createVideo,
    payload: buildVideoForm(payload),
  });

  return {
    status: response?.status,
    message: response?.data?.msg || response?.data?.message || response?.statusText,
    video: response?.data?.video,
  };
};

export const UpdateVideo = async (
  id: string,
  payload: VideoPayload
): Promise<VideoReturn> => {
  const response = await patchRequest({
    route: Requests.update.updateVideo,
    pathVariables: { id },
    payload: buildVideoForm(payload),
  });

  return {
    status: response?.status,
    message: response?.data?.msg || response?.data?.message || response?.statusText,
    video: response?.data?.video,
  };
};

export const DeleteVideo = async (id: string): Promise<VideoReturn> => {
  const response = await deleteRequest({
    route: Requests.delete.deleteVideo,
    pathVariables: { id },
  });

  return {
    status: response?.status,
    message: response?.data?.msg,
    video: response?.data?.video,
  };
};
